import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AuthContext = createContext(null);

// Use sessionStorage for tokens (more secure than localStorage against XSS)
const TOKEN_KEY = 'admin_token';

export const AuthProvider = ({ children }) => {
  const [isAdmin, setIsAdmin] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [token, setToken] = useState(() => sessionStorage.getItem(TOKEN_KEY));

  const verifyToken = useCallback(async () => {
    const savedToken = sessionStorage.getItem(TOKEN_KEY);
    if (!savedToken) {
      setIsLoading(false);
      return;
    }

    try {
      await axios.get(`${API}/admin/verify`, {
        headers: { Authorization: `Bearer ${savedToken}` }
      });
      setIsAdmin(true);
      setToken(savedToken);
    } catch (error) {
      console.error('Token verification failed:', error);
      sessionStorage.removeItem(TOKEN_KEY);
      setIsAdmin(false);
      setToken(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    verifyToken();
  }, [verifyToken]);

  const login = async (password) => {
    try {
      const response = await axios.post(`${API}/admin/login`, { password });
      const { token: newToken } = response.data;
      sessionStorage.setItem(TOKEN_KEY, newToken);
      setToken(newToken);
      setIsAdmin(true);
      return { success: true };
    } catch (error) {
      console.error('Login failed:', error);
      return { success: false, error: error.response?.data?.detail || 'Erreur de connexion' };
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/admin/logout`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (error) {
      console.error('Logout error:', error);
    }
    sessionStorage.removeItem(TOKEN_KEY);
    setToken(null);
    setIsAdmin(false);
  };

  const getAuthHeaders = () => {
    if (!token) return {};
    return { Authorization: `Bearer ${token}` };
  };

  return (
    <AuthContext.Provider value={{ isAdmin, isLoading, login, logout, token, getAuthHeaders }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
